import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface WOAdjustment {
  id: string;
  company_id: string;
  work_order_id: string;
  adjustment_type: string;
  direction: string;
  amount: number;
  description: string;
  unit_id?: string | null;
  sov_line_id?: string | null;
  responsible_party_id?: string | null;
  caused_by_vendor_id?: string | null;
  linked_adjustment_id?: string | null;
  status: string;
  dispute_reason?: string | null;
  dispute_deadline?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  applied_to_invoice_id?: string | null;
  applied_at?: string | null;
  notes?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export const useWOAdjustments = (workOrderId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["wo_adjustments", workOrderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wo_adjustments" as any)
        .select("*")
        .eq("work_order_id", workOrderId!)
        .neq("status", "voided")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as WOAdjustment[];
    },
    enabled: !!user && !!workOrderId,
  });
};

export const useProjectAdjustments = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["project_adjustments", projectId],
    queryFn: async () => {
      // Get WO ids for this project
      const { data: wos, error: woErr } = await supabase
        .from("work_orders")
        .select("id, number, vendors(name)")
        .eq("project_id", projectId!);
      if (woErr) throw woErr;
      const woIds = (wos ?? []).map(w => w.id);
      if (!woIds.length) return { adjustments: [], workOrders: wos ?? [] };

      const { data, error } = await supabase
        .from("wo_adjustments" as any)
        .select("*")
        .in("work_order_id", woIds)
        .neq("status", "voided")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return { adjustments: (data ?? []) as unknown as WOAdjustment[], workOrders: wos ?? [] };
    },
    enabled: !!user && !!projectId,
  });
};

export const usePendingAdjustments = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["pending_adjustments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wo_adjustments" as any)
        .select("*, work_orders(number, project_id, projects(name))" as any)
        .eq("status", "pending_approval")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user,
  });
};

export const useCreateAdjustment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (adj: Partial<WOAdjustment> & { company_id: string; work_order_id: string }) => {
      const { data, error } = await supabase
        .from("wo_adjustments" as any)
        .insert(adj as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["wo_adjustments", v.work_order_id] });
      qc.invalidateQueries({ queryKey: ["project_adjustments"] });
      qc.invalidateQueries({ queryKey: ["pending_adjustments"] });
    },
  });
};

export const useTransitionAdjustment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newStatus, reason }: { id: string; newStatus: string; reason?: string }) => {
      const { error } = await supabase.rpc("transition_adjustment_status" as any, {
        p_adjustment_id: id,
        p_new_status: newStatus,
        p_reason: reason ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wo_adjustments"] });
      qc.invalidateQueries({ queryKey: ["project_adjustments"] });
      qc.invalidateQueries({ queryKey: ["pending_adjustments"] });
      qc.invalidateQueries({ queryKey: ["approval_counts"] });
    },
  });
};

/** Get adjustment totals for a work order */
export function computeAdjustmentTotals(adjustments: WOAdjustment[]) {
  const active = adjustments.filter(a => ["approved", "applied"].includes(a.status));
  const pending = adjustments.filter(a => a.status === "pending_approval");
  const disputed = adjustments.filter(a => a.status === "disputed");

  const debits = active.filter(a => a.direction === "debit").reduce((s, a) => s + Number(a.amount), 0);
  const credits = active.filter(a => a.direction === "credit").reduce((s, a) => s + Number(a.amount), 0);
  const pendingDebits = pending.filter(a => a.direction === "debit").reduce((s, a) => s + Number(a.amount), 0);
  const pendingCredits = pending.filter(a => a.direction === "credit").reduce((s, a) => s + Number(a.amount), 0);
  const disputedDebits = disputed.filter(a => a.direction === "debit").reduce((s, a) => s + Number(a.amount), 0);

  const backcharges = active.filter(a => a.direction === "debit" && ["backcharge", "damage"].includes(a.adjustment_type))
    .reduce((s, a) => s + Number(a.amount), 0);
  const rework = active.filter(a => a.direction === "debit" && a.adjustment_type === "rework")
    .reduce((s, a) => s + Number(a.amount), 0);
  const bonuses = active.filter(a => a.direction === "credit")
    .reduce((s, a) => s + Number(a.amount), 0);

  return {
    debits,
    credits,
    net: credits - debits,
    backcharges,
    rework,
    bonuses,
    pendingDebits,
    pendingCredits,
    disputedDebits,
    pendingCount: pending.length,
    disputedCount: disputed.length,
  };
}
